import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { size } from 'lodash/fp';
import {
  Switch,
  Redirect,
  Route,
  NavLink,
  withRouter,
} from 'react-router-dom';
import { timeframeLatest, timeframeAll } from 'selectors/indicator/timeframe';
import {
  infoSelector,
  shortTitleSelector,
  titleSelector,
} from 'selectors/indicator';
import classNames from 'classnames';
import getViewProperties from 'services/getViewProperties';

// Components
import IndicatorHeader from 'components/indicatorHeader';
import HighmapsWrapper from 'components/highmapsWrapper';
import CountyHighmapsWrapper from 'components/countyHighmapsWrapper';
import TimeframeScrubber from 'components/timeframeScrubber';
import Tabs from 'components/tabs';
import Button from 'components/button';
import EmbedControls from 'components/embedControls';
import FlyoutButton from 'components/flyoutButton';
import MapViewTable from 'components/mapViewTable';
import ShareControls from 'components/shareControls';
import Copyright from 'components/copyright';
import TextBlock from 'components/textBlock';

// Helpers
import downloadLink from 'services/downloadLink';

// Styles
import styles from './mapView.scss';

class MapView extends Component {
  constructor(props) {
    super(props);

    this.changeActiveTimeframe = this.changeActiveTimeframe.bind(this);
    this.renderUs = this.renderUs.bind(this);
    this.renderWorld = this.renderWorld.bind(this);
    this.renderControls = this.renderControls.bind(this);
  }

  changeActiveTimeframe(value) {
    const {
      history,
      match: {
        params: {
          category,
          indicatorName,
          context,
        },
      },
    } = this.props;
    history.push({
      pathname: `/${category}/indicator/${indicatorName}/map/${context}/${value}`,
    });
  }

  /**
   * Renders controls above the map (download, embed, share).
   * @returns {XML}
   */
  renderControls() {
    const {
      activeTimeframe,
      match: {
        params: {
          category,
          context,
          indicatorName,
          renderType,
        },
      },
      title,
    } = this.props;

    // Don't render controls if we are in an embed or export context.
    const { isMinimalView } = getViewProperties(renderType);
    if (isMinimalView) {
      return null;
    }

    return (
      <div className={styles.controls}>
        <ul className={styles.downloadControls}>
          <li>
            <Button
              action={downloadLink({
                dataView: category,
                slug: indicatorName,
                timeframe: activeTimeframe.value,
                type: 'csv',
              })}
              hardLink
              icon="download"
              label="Download Data"
            />
          </li>
          <li>
            <Button
              action={downloadLink({
                dataView: category,
                primaryView: 'map',
                secondaryView: context,
                slug: indicatorName,
                timeframe: activeTimeframe.value,
                type: 'image',
              })}
              hardLink
              icon="download"
              label="Download Image"
            />
          </li>
        </ul>
        <ul className={styles.shareControls}>
          <li>
            <FlyoutButton
              label="Embed"
              type="knockout"
            >
              <EmbedControls
                dataView={category}
                indicatorSubView={context}
                indicatorView="map"
                slug={indicatorName}
                timeframe={activeTimeframe.value || -1}
              />
            </FlyoutButton>
          </li>
          <li>
            <FlyoutButton
              label="Share"
              type="knockout"
            >
              <ShareControls
                dataView={category}
                indicatorSubView={context}
                indicatorView="map"
                slug={indicatorName}
                timeframe={activeTimeframe.value || -1}
                title={title}
              />
            </FlyoutButton>
          </li>
        </ul>
      </div>
    );
  }

  renderWorld() {
    const {
      activeTimeframe,
      info: {
        sources = '',
      },
      location: {
        pathname,
      },
      match: {
        params: {
          category,
          indicatorName,
          renderType,
        },
      },
      shortTitle,
    } = this.props;

    const {
      isEmbedded,
      isExporting,
      isMinimalView,
    } = getViewProperties(renderType);

    return (
      <section
        className={classNames(
          styles.mapView,
          {
            [styles.mapViewEmbedded]: isEmbedded,
          }
        )}
      >
        {!isMinimalView &&
          <Tabs>
            <NavLink to={`/${category}/indicator/${indicatorName}/map/country`}>
              Country
            </NavLink>
            <NavLink to={`/${category}/indicator/${indicatorName}/map/subregion`}>
              Subregion
            </NavLink>
            <NavLink to={`/${category}/indicator/${indicatorName}/map/region`}>
              Region
            </NavLink>
          </Tabs>
        }
        <div className={styles.container}>
          {this.renderControls()}
          <h3 className={styles.title}>{shortTitle}</h3>
          <div className={styles.mapWrapper}>
            <Switch>
              <Route
                path="/:renderType?/:category/indicator/:indicatorName/map/country"
                render={(props) => (
                  <HighmapsWrapper
                    {...props}
                    timeframe={activeTimeframe.value}
                    type="world"
                  />
                )}
              />
              <Route
                path="/:renderType?/:category/indicator/:indicatorName/map/region"
                render={(props) => (
                  <HighmapsWrapper
                    {...props}
                    group="region"
                    timeframe={activeTimeframe.value}
                    type="world"
                  />
                )}
              />
              <Route
                path="/:renderType?/:category/indicator/:indicatorName/map/subregion"
                render={(props) => (
                  <HighmapsWrapper
                    {...props}
                    group="subregion"
                    timeframe={activeTimeframe.value}
                    type="world"
                  />
                )}
              />
              <Route
                exact
                path="/:category/indicator/:indicatorName/map/"
                render={() => (
                  <Redirect
                    to={`/${category}/indicator/${indicatorName}/map/country`}
                  />
                )}
              />
            </Switch>
          </div>
          {isMinimalView && sources &&
            <TextBlock
              title="Sources"
              text={sources}
            />
          }
          <Copyright
            isEmbedded={isEmbedded}
            isExporting={isExporting}
            originalPath={pathname}
          />
        </div>
      </section>
    );
  }

  renderUs() {
    const {
      activeTimeframe,
      info: {
        sources = '',
      },
      location: {
        pathname,
      },
      match: {
        params: {
          category,
          indicatorName,
          renderType,
          year,
        },
      },
      shortTitle,
    } = this.props;

    const {
      isEmbedded,
      isExporting,
      isMinimalView,
    } = getViewProperties(renderType);

    return (
      <section
        className={classNames(
          styles.mapView,
          {
            [styles.mapViewEmbedded]: isEmbedded,
          }
        )}
      >
        {!isMinimalView &&
          <Tabs>
            <NavLink to={`/${category}/indicator/${indicatorName}/map/state/${year}`}>
              State
            </NavLink>
            <NavLink to={`/${category}/indicator/${indicatorName}/map/county/${year}`}>
              County
            </NavLink>
          </Tabs>
        }
        <div className={styles.container}>
          {this.renderControls()}
          <h3 className={styles.title}>
            {shortTitle} ({activeTimeframe.label})
          </h3>
          <div className={styles.mapWrapper}>
            <Switch>
              <Route
                path="/:renderType?/:category/indicator/:indicatorName/map/state"
                render={(props) => (
                  <HighmapsWrapper
                    {...props}
                    timeframe={activeTimeframe.value}
                    type="state"
                  />
                )}
              />
              <Route
                path="/:renderType?/:category/indicator/:indicatorName/map/county"
                render={() => (
                  <CountyHighmapsWrapper
                    timeframe={activeTimeframe.value}
                  />
                )}
              />
              <Route
                exact
                path="/:category/indicator/:indicatorName/map/"
                render={() => (
                  <Redirect
                    to={`/${category}/indicator/${indicatorName}/map/state/${activeTimeframe.value}`}
                  />
                )}
              />
            </Switch>
          </div>
          {isMinimalView && sources &&
            <TextBlock
              title="Sources"
              text={sources}
            />
          }
          <Copyright
            isEmbedded={isEmbedded}
            isExporting={isExporting}
            originalPath={pathname}
          />
        </div>
      </section>
    );
  }

  render() {
    const {
      activeTimeframe,
      timeframes,
      match: {
        params: {
          category,
          indicatorName,
          renderType,
        },
      },
    } = this.props;

    const { isMinimalView } = getViewProperties(renderType);

    return (
      <div>
        <IndicatorHeader>
          { 1 < size(timeframes) && (
            <TimeframeScrubber
              activeTimeframe={activeTimeframe.value}
              timeframes={timeframes}
              onClick={this.changeActiveTimeframe}
            />
          )}
        </IndicatorHeader>
        {'international' === category ? this.renderWorld() : this.renderUs()}
        {!isMinimalView &&
          <MapViewTable
            category={category}
            indicatorName={indicatorName}
            timeframes={activeTimeframe.value}
            isTwoCol
          />
        }
      </div>
    );
  }
}

MapView.propTypes = {
  history: PropTypes.any.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      category: PropTypes.string.isRequired,
      indicatorName: PropTypes.string.isRequired,
      context: PropTypes.string,
      year: PropTypes.string,
    }).isRequired,
  }).isRequired,
  shortTitle: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  activeTimeframe: PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  latestTimeframe: PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  timeframes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  info: PropTypes.shape({
    sources: PropTypes.string,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  latestTimeframe: timeframeLatest(state),
  timeframes: timeframeAll(state),
  shortTitle: shortTitleSelector(state),
  title: titleSelector(state),
  info: infoSelector(state),
});

const mergeProps = (state, dispatch, own) => {
  const { match: { params: { year = null } } } = own;
  return {
    ...state,
    ...own,
    activeTimeframe: state.timeframes.find((timeframe) =>
      parseInt(year, 10) === timeframe.value) || state.latestTimeframe,
  };
};

export default withRouter(connect(mapStateToProps, null, mergeProps)(MapView));



// WEBPACK FOOTER //
// ./app/components/mapView/index.js